// Écran de jeu : en-tête Scene (grande illustration sur Récit, compacte ailleurs) + nom du lieu + heure + menu (quitter, notifications) ; StatusBar ; bandeau unique d'invitation aux
// notifications ; onglets en bas (Récit, Carte, Fiche, Sac, Compagnons, Chronique) avec pastilles ;
// onglet Récit = Feed + CombatPanel/DungeonPanel si actifs + ActionBar. Genèse en cours → écran d'attente animé. Mort → mémorial + CharacterCreation mode rebirth.
import { useEffect, useRef, useState } from "preact/hooks";
import type { CharacterView, ClientView } from "../../shared/types";
import { disconnect, send, useAppState } from "../store";
import { Scene } from "../components/Scene";
import { StatusBar } from "../components/StatusBar";
import { Feed } from "../components/Feed";
import { CombatPanel } from "../components/CombatPanel";
import { DungeonPanel } from "../components/DungeonPanel";
import { ActionBar } from "../components/ActionBar";
import { WorldMap } from "../components/WorldMap";
import { Sheet } from "../components/Sheet";
import { Inventory } from "../components/Inventory";
import { Companions } from "../components/Companions";
import { Chronicle } from "../components/Chronicle";
import { NotifyBanner, NotifyButton } from "../components/NotifyButton";
import { MusicToggle } from "../components/MusicToggle";
import { ChatGptMenu } from "../components/ChatGptMenu";
import { Portrait } from "../components/Portrait";
import { CharacterCreation } from "./CharacterCreation";
import "./Game.css";

type TabId = "recit" | "carte" | "fiche" | "sac" | "compagnons" | "chronique";

const TABS: { id: TabId; icon: string; label: string }[] = [
  { id: "recit", icon: "📜", label: "Récit" },
  { id: "carte", icon: "🗺️", label: "Carte" },
  { id: "fiche", icon: "🧙", label: "Fiche" },
  { id: "sac", icon: "🎒", label: "Sac" },
  { id: "compagnons", icon: "🔥", label: "Compagnons" },
  { id: "chronique", icon: "📖", label: "Chronique" },
];

/** Hauteur de l'illustration du lieu : grande sur l'onglet Récit, réduite sur les autres onglets. */
const HAUTEUR_ENTETE = 210;
const HAUTEUR_ENTETE_COMPACTE = 96;
/** Petit iPhone, ou Safari avec ses barres : l'illustration compacte laisse au récit de quoi se lire. */
const ECRAN_COURT = "(max-height: 760px)";

const GENESE_PHRASES = [
  "Le Conteur tisse le monde…",
  "Des routes se dessinent sur une carte encore vierge…",
  "Des villages s'allument un à un dans la brume…",
  "Les dieux anciens murmurent dans les ruines…",
  "Une menace se réveille sous les montagnes…",
  "Les premiers héros approchent du feu…",
];

// ───────── Heure de Paris (Intl, sans horloge système pour le rendu) ─────────

const PARIS_CLOCK = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "numeric", minute: "2-digit", hourCycle: "h23" });

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function parisClock(at: number): { h: number; m: number } {
  const parts = PARIS_CLOCK.formatToParts(new Date(at));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return { h, m };
}

function heureAffichee(now: number): string {
  const { h, m } = parisClock(now);
  return `${pad2(h)}:${pad2(m)}`;
}

/** Jour ou nuit, selon l'heure de Paris (nuit de jeu = 22 h – 7 h). */
function momentDuJour(now: number): { icon: string; label: string } {
  const { h } = parisClock(now);
  if (h >= 6 && h < 8) return { icon: "🌅", label: "aube" };
  if (h >= 8 && h < 19) return { icon: "☀️", label: "jour" };
  if (h >= 19 && h < 22) return { icon: "🌇", label: "crépuscule" };
  return { icon: "🌙", label: "nuit" };
}

/**
 * Horloge de l'écran : l'heure du serveur, recalée localement entre deux mises à jour
 * et rafraîchie toutes les 15 s (heure affichée, compte à rebours).
 */
function useGameNow(serverNow: number | null): number {
  const [, force] = useState(0);
  const anchor = useRef<{ server: number; local: number } | null>(null);
  if (serverNow !== null && anchor.current?.server !== serverNow) {
    anchor.current = { server: serverNow, local: Date.now() };
  }
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);
  if (serverNow === null || anchor.current === null) return Date.now();
  return anchor.current.server + (Date.now() - anchor.current.local);
}

// ───────── Dernier chat vu (état local par monde) ─────────

function chatSeenKey(code: string): string {
  return `veillee:chat-vu:${code}`;
}

function readChatSeen(code: string): number | null {
  try {
    const raw = localStorage.getItem(chatSeenKey(code));
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeChatSeen(code: string, at: number): void {
  try {
    localStorage.setItem(chatSeenKey(code), String(at));
  } catch {
    // Stockage indisponible (navigation privée) : on oublie simplement le dernier vu.
  }
}

// ───────── Écrans particuliers ─────────

function GeneseEcran(props: { nom: string }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % GENESE_PHRASES.length), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div class="game game-genese" role="status" aria-live="polite">
      <div class="game-genese-feu" aria-hidden="true">
        <span class="game-genese-braise" />
        <span class="game-genese-flamme game-genese-flamme-1" />
        <span class="game-genese-flamme game-genese-flamme-2" />
        <span class="game-genese-flamme game-genese-flamme-3" />
        <span class="game-genese-flamme game-genese-flamme-4" />
        <span class="game-genese-flamme game-genese-flamme-5" />
      </div>
      <h1 class="game-genese-titre">{props.nom}</h1>
      <p key={index} class="game-genese-texte">
        {GENESE_PHRASES[index]}
      </p>
    </div>
  );
}

function MemorialEcran(props: { view: ClientView; character: CharacterView }) {
  const { view, character } = props;
  return (
    <div class="game game-mort">
      <header class="game-mort-entete">
        <span class="game-mort-monde">{view.world.name}</span>
        <button type="button" class="btn btn-ghost btn-small" onClick={() => disconnect()}>
          Accueil
        </button>
      </header>
      <main class="game-corps">
        <section class="card-parchment game-memorial" aria-label="Mémorial">
          <Portrait classId={character.classId} color={character.appearance.color} sigil={character.appearance.sigil} size={96} dead />
          <h1 class="game-memorial-nom">{character.name}</h1>
          <p class="game-memorial-classe">
            {character.className} · niveau {character.level}
          </p>
          <p class="game-memorial-texte">
            Ta veillée s'achève ici. Ton histoire rejoint la chronique des héros tombés, et le monde se souviendra de toi près du feu.
            Un héritier peut reprendre le flambeau.
          </p>
        </section>
        <CharacterCreation mode="rebirth" />
      </main>
    </div>
  );
}

// ───────── Écran de jeu ─────────

function useEcranCourt(): boolean {
  const [court, setCourt] = useState(() => window.matchMedia(ECRAN_COURT).matches);
  useEffect(() => {
    const requete = window.matchMedia(ECRAN_COURT);
    const suivre = (): void => setCourt(requete.matches);
    requete.addEventListener("change", suivre);
    return () => requete.removeEventListener("change", suivre);
  }, []);
  return court;
}

export function Game() {
  const ecranCourt = useEcranCourt();
  const { view, gm } = useAppState();
  const [tab, setTab] = useState<TabId>("recit");
  const [menuOpen, setMenuOpen] = useState(false);
  const [seenAt, setSeenAt] = useState<number | null>(null);
  const bodyRef = useRef<HTMLElement>(null);
  const now = useGameNow(view ? view.now : null);

  const code = view ? view.world.code : "";
  const latestChatAt = view ? view.chat.reduce((max, m) => Math.max(max, m.at), 0) : 0;

  // Premier passage sur ce monde : le chat déjà présent est considéré comme lu.
  useEffect(() => {
    if (!view || seenAt !== null) return;
    const value = readChatSeen(code) ?? latestChatAt;
    setSeenAt(value);
    writeChatSeen(code, value);
  }, [code, seenAt, latestChatAt]);

  // Ouvrir l'onglet Compagnons : tout le chat est lu.
  useEffect(() => {
    if (tab === "compagnons" && seenAt !== null && latestChatAt > seenAt) {
      setSeenAt(latestChatAt);
      writeChatSeen(code, latestChatAt);
    }
  }, [tab, seenAt, latestChatAt, code]);

  // Changer d'onglet remonte en haut (le récit, lui, suit les nouveaux événements).
  useEffect(() => {
    if (tab !== "recit" && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [tab]);

  if (!view) {
    return (
      <div class="game game-chargement" role="status">
        <p class="game-chargement-texte">Le feu reprend…</p>
      </div>
    );
  }

  const character = view.character;

  if (view.world.status === "genese") {
    return <GeneseEcran nom={view.world.name} />;
  }

  if (character && character.lifeState === "mort") {
    return <MemorialEcran view={view} character={character} />;
  }

  if (!character) {
    return (
      <div class="game game-chargement" role="status">
        <p class="game-chargement-texte">Ton personnage se fait attendre…</p>
      </div>
    );
  }

  const location = view.location;
  const moment = momentDuJour(now);
  const openSollicitations = view.sollicitations.filter((s) => s.status === "ouverte").length;
  const unread = seenAt === null ? 0 : view.chat.filter((m) => m.at > seenAt && m.playerId !== view.me.playerId).length;
  const showDungeon = !!view.dungeon || !!location?.dungeonId;
  const compact = tab !== "recit" || ecranCourt;

  return (
    <div class="game">
      <header class={compact ? "game-entete game-entete-compact" : "game-entete"}>
        <div class="game-entete-scene">
          <Scene
            biome={location?.biome ?? "plaine"}
            kind={location?.kind ?? "clairiere"}
            name={location?.name ?? view.world.name}
            seed={location?.id ?? view.world.id}
            now={now}
            danger={location?.danger ?? 0}
            inCombat={!!view.combat}
            height={compact ? HAUTEUR_ENTETE_COMPACTE : HAUTEUR_ENTETE}
          />
        </div>
        <div class="game-entete-voile" aria-hidden="true" />

        <button
          type="button"
          class="game-menu-bouton"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          ☰
        </button>
        <p class="game-heure" title={`Heure de Paris, ${moment.label}`}>
          <span aria-hidden="true">{moment.icon}</span> {heureAffichee(now)}
        </p>
        <div class="game-entete-lieu">
          <h1 class="game-lieu">{location ? location.name : "Sur la route"}</h1>
          <p class="game-monde">{view.world.name}</p>
        </div>

        {menuOpen && (
          <>
            <button type="button" class="game-menu-voile" aria-label="Fermer le menu" onClick={() => setMenuOpen(false)} />
            <div class="game-menu" role="menu">
              <button
                type="button"
                class="btn btn-ghost game-menu-item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  disconnect();
                }}
              >
                Retour à l'accueil
              </button>
              <MusicToggle variant="menu" />
              <ChatGptMenu view={view} />
              <NotifyButton variant="menu" />
            </div>
          </>
        )}
      </header>

      <StatusBar c={character} now={now} />

      <NotifyBanner />

      <main ref={bodyRef} class={`game-corps game-corps-${tab}`}>
        {view.world.status === "erreur" && (
          <div class="game-bandeau game-bandeau-erreur" role="alert">
            <span aria-hidden="true">⚠️</span>
            <span>Le Conteur a rencontré un souci. {view.world.statusMessage ?? "Réessaie un peu plus tard."}</span>
          </div>
        )}

        {character.lifeState === "egare" && (
          <div class="game-bandeau game-bandeau-egare" role="status">
            <span aria-hidden="true">🌫️</span>
            <span>
              Tu es égaré, tu erres dans le récit des autres. Ta prochaine action te ramènera au monde avec une seule flamme et une cicatrice d'oubli.
            </span>
          </div>
        )}

        {tab === "recit" && (
          <div class="game-recit">
            <Feed events={view.feed} myCharacterId={character.id} now={now} />
            {view.combat && <CombatPanel view={view} />}
            {showDungeon && <DungeonPanel view={view} />}
          </div>
        )}
        {tab === "carte" && <WorldMap view={view} onTravel={(id) => send({ t: "travel", toLocationId: id })} />}
        {tab === "fiche" && <Sheet view={view} />}
        {tab === "sac" && <Inventory view={view} />}
        {tab === "compagnons" && <Companions view={view} />}
        {tab === "chronique" && <Chronicle view={view} />}
      </main>

      {tab === "recit" && (
        <div class="game-action">
          {gm.busy && (
            <div class="game-plume" role="status" aria-live="polite">
              <span class="game-plume-icone" aria-hidden="true">🪶</span>
              <span>
                Le Conteur écrit<span class="game-plume-points" aria-hidden="true">…</span>
              </span>
            </div>
          )}
          <ActionBar view={view} />
        </div>
      )}

      <nav class="game-onglets" role="tablist" aria-label="Sections du jeu">
        {TABS.map((t) => {
          const selected = tab === t.id;
          const dot = (t.id === "recit" && openSollicitations > 0) || (t.id === "compagnons" && unread > 0);
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={selected}
              class={selected ? "game-onglet game-onglet-actif" : "game-onglet"}
              onClick={() => setTab(t.id)}
            >
              <span class="game-onglet-icone" aria-hidden="true">{t.icon}</span>
              <span class="game-onglet-libelle">{t.label}</span>
              {dot && (
                <span
                  class="game-onglet-pastille"
                  role="img"
                  aria-label={t.id === "recit" ? "Une question attend ta réponse" : "De nouveaux messages"}
                />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
